import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { api } from '@/App';
import { logger } from '@/utils/logger';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSubscribing(true);
    try {
      const res = await api.post('/newsletter/subscribe', { email: trimmed });
      if (res.data.status === 'already_subscribed') {
        toast.info(res.data.message);
      } else {
        toast.success(res.data.message);
      }
      setEmail('');
    } catch (error) {
      logger.error('Newsletter subscribe failed:', error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Subscription failed. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer style={{background: '#20212B', color: 'white'}}>
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-6 h-6" style={{color: '#9E6752'}} fill="#9E6752" />
              <span className="text-2xl" style={{fontFamily: 'Playfair Display, serif', fontWeight: '600'}}>Mementa</span>
            </div>
            <p className="text-sm mb-2" style={{color: '#FED7A5'}}>Thoughtful Gift Discovery</p>
            <p className="text-sm" style={{color: 'rgba(255,255,255,0.7)'}}>
              Find the perfect gifts with care and thoughtfulness.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4" style={{color: '#FED7A5'}}>Discover</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/browse" style={{color: 'rgba(255,255,255,0.8)'}} className="hover:text-white">Gift Collection</Link></li>
              <li><Link to="/keepsakes" style={{color: 'rgba(255,255,255,0.8)'}} className="hover:text-white">Your Keepsakes</Link></li>
              <li><Link to="/about" style={{color: 'rgba(255,255,255,0.8)'}} className="hover:text-white">About Mementa</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4" style={{color: '#FED7A5'}}>Contact & Support</h3>
            <ul className="space-y-2 text-sm">
              <li><a href="mailto:support@mementa.gifts" style={{color: 'rgba(255,255,255,0.8)'}} className="hover:text-white">support@mementa.gifts</a></li>
              <li><a href="/terms" style={{color: 'rgba(255,255,255,0.8)'}} className="hover:text-white">Terms of Service</a></li>
              <li><a href="/privacy" style={{color: 'rgba(255,255,255,0.8)'}} className="hover:text-white">Privacy Policy</a></li>
              <li><a href="/cookies" style={{color: 'rgba(255,255,255,0.8)'}} className="hover:text-white">Cookie Policy</a></li>
              <li><a href="/affiliate-disclosure" style={{color: 'rgba(255,255,255,0.8)'}} className="hover:text-white">Affiliate Disclosure</a></li>
              <li>
                <button
                  data-testid="footer-cookie-prefs-btn"
                  onClick={() => window.dispatchEvent(new CustomEvent('mementa:open-cookie-prefs'))}
                  style={{color: 'rgba(255,255,255,0.8)'}}
                  className="hover:text-white"
                >
                  Cookie Preferences
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4" style={{color: '#FED7A5'}}>Media & Press</h3>
            <ul className="space-y-2 text-sm">
              <li><a href="mailto:media@mementa.gifts" style={{color: 'rgba(255,255,255,0.8)'}} className="hover:text-white">media@mementa.gifts</a></li>
              <li><a href="mailto:affiliate@mementa.gifts" style={{color: 'rgba(255,255,255,0.8)'}} className="hover:text-white">affiliate@mementa.gifts</a></li>
              <li><a href="#" style={{color: 'rgba(255,255,255,0.8)'}} className="hover:text-white">Contact Our Team</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t pt-8" style={{borderColor: 'rgba(255,255,255,0.1)'}}>
          <p
            data-testid="footer-affiliate-disclosure"
            className="text-xs mb-6 text-center leading-relaxed"
            style={{color: 'rgba(255,255,255,0.5)'}}
          >
            Affiliate Disclosure: Mementa participates in affiliate programs, including with Etsy
            via Rakuten Advertising. We may earn a commission from qualifying purchases made through
            links on this site, at no additional cost to you.{' '}
            <a href="/affiliate-disclosure" className="underline hover:text-white" style={{color: 'rgba(255,255,255,0.6)'}}>Learn more</a>
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm" style={{color: 'rgba(255,255,255,0.6)'}}>
            <p>© 2026 Mementa. All rights reserved.</p>
            <div className="flex items-center gap-2">
              <span>Thoughtful Gift Platform</span>
              <span>•</span>
              <span>Platform Online</span>
            </div>
            <p>Made with love for thoughtful gift-givers</p>
          </div>
        </div>
      </div>

      <div className="py-16" style={{background: '#20212B'}}>
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h3 className="text-3xl mb-4" style={{fontFamily: 'Playfair Display, serif', color: 'white', fontWeight: '600'}}>
            Stay Updated
          </h3>
          <p className="text-base mb-8" style={{color: 'rgba(255,255,255,0.8)'}}>
            Get the latest gift ideas and special offers delivered to your inbox
          </p>
          <form className="max-w-md mx-auto" onSubmit={handleSubscribe} noValidate data-testid="newsletter-form">
            <label className="block text-sm mb-2 text-left" style={{color: 'white'}}>Email Address</label>
            <div className="flex gap-4">
              <input 
                type="email" 
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input"
                className="flex-1 px-4 py-3 rounded-lg text-sm"
                style={{background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: 'white'}}
              />
              <Button
                type="submit"
                disabled={subscribing}
                data-testid="newsletter-subscribe-button"
                style={{background: '#9E6752', color: 'white', borderRadius: '8px', padding: '12px 24px'}}
              >
                {subscribing ? 'Subscribing…' : 'Subscribe'}
              </Button>
            </div>
            <p className="text-xs mt-3" style={{color: 'rgba(255,255,255,0.6)'}}>
              We will send you a confirmation email. You can unsubscribe at any time.
            </p>
          </form>
        </div>
      </div>

      <div className="py-8" style={{background: '#20212B', borderTop: '1px solid rgba(255,255,255,0.1)'}}>
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h4 className="text-xl mb-3" style={{fontFamily: 'Playfair Display, serif', color: 'white', fontWeight: '600'}}>
            Connect with Our Curators
          </h4>
          <a href="mailto:support@mementa.gifts" className="text-base" style={{color: '#FED7A5'}}>
            support@mementa.gifts
          </a>
        </div>
      </div>
    </footer>
  );
}
